<template>
  <section>
    <PageTitle>お気に入り</PageTitle>
    <PageContent v-if="favoriteItemList.length === 0">
      <Container>
        <NoticeBox
          type="error"
          :borderless="true"
          class="space-b-2 text-bold"
        >
          現在、お気に入りアイテムがありません
        </NoticeBox>
        <p class="text-paragraph text-xs">
          現在表示されるお気に入りアイテムがありません。
          <br class="mobile-only" />
          お気に入りアイテムがある場合は、この画面に表示されます。
        </p>
      </Container>
    </PageContent>
    <PageContent v-else>
      <Container>
        <NoticeBox
          v-if="isUsageLimitOver"
          type="info"
          :icon-class="['fas', 'fa-lock']"
        >
          利用制限日を過ぎたため、変更できません
        </NoticeBox>
        <div class="form-group">
          <p class="text-paragraph text-xs">
            見積シミュレーションの金額と異なる場合がございますのでご了承ください。
          </p>
        </div>
        <div class="favorite-item-list-wrapper">
          <div
            v-for="groupRow in favoriteList"
            v-bind:key="groupRow.groupId"
          >
            <Headline :level="2">
              {{$domUtils.decode(groupRow.groupName)}}
            </Headline>
            <div
              v-for="subgroupRow in groupRow.subgroupList"
              v-bind:key="subgroupRow.subgroupId"
            >
              <Headline :level="3">
                {{$domUtils.decode(subgroupRow.subgroupName)}}
              </Headline>
              <ul class="favorite-item-list">
                <li
                  v-for="row in subgroupRow.itemList"
                  :key="row.bridal_item_id"
                >
                  <BridalItemCard
                    :id="`favorite-item-${row.bridal_item_id}`"
                    :item="favoriteItem(row.bridal_item_id)"
                    :favoriteButtonShow="true"
                    :simulationButtonShow
                      ="favoriteItem(row.bridal_item_id).favorited"
                    :collageButtonShow
                      ="favoriteItem(row.bridal_item_id)
                          .collage_display_target_flag
                        && favoriteItem(row.bridal_item_id).simulation_target"
                    :favoriteButtonDisabled="isUsageLimitOver
                      || favoriteItem(row.bridal_item_id).simulation_target"
                    :simulationButtonDisabled="isUsageLimitOver"
                    :collageButtonDisabled="isUsageLimitOver
                      || favoriteItem(row.bridal_item_id).collage_target"
                    @click-image="showFavoriteModal(row.bridal_item_id)"
                    @toggle-favorite="onToggleFavorite"
                    @toggle-simulation="onToggleSimulation"
                    @toggle-collage="onToggleCollage"
                  ></BridalItemCard>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Container>
    </PageContent>
    <SimpleModal id="favorite-item-modal" ref="favoriteItemModal">
      <template #default>
        <BridalItemCard
          class="in-modal"
          :item="favoriteItem(favoriteItemModalTargetId)"
          :favoriteButtonShow="true"
          :simulationButtonShow
            ="favoriteItem(favoriteItemModalTargetId).favorited"
          :collageButtonShow
            ="favoriteItem(favoriteItemModalTargetId)
                .collage_display_target_flag
              && favoriteItem(favoriteItemModalTargetId).simulation_target"
          :favoriteButtonDisabled="isUsageLimitOver
            || favoriteItem(favoriteItemModalTargetId).simulation_target"
          :simulationButtonDisabled="isUsageLimitOver"
          :collageButtonDisabled="isUsageLimitOver
            || favoriteItem(favoriteItemModalTargetId).collage_target"
          @toggle-favorite="onToggleFavorite"
          @toggle-simulation="onToggleSimulation"
          @toggle-collage="onToggleCollage"
        ></BridalItemCard>
      </template>
    </SimpleModal>
    <Modal id="simulation-fixed" @ok="$modal.close('simulation-fixed')">
      <template #default>
        見積確定済みのため変更できません。
      </template>
    </Modal>
  </section>
</template>

<script>
import { renderError, renderExeclusiveControlError } from '@/utils/error'
import { SCHEME, getDomain } from '@/api/theme'
import bridalItemApi from '@/api/bridal-item'
import NoticeBox from '@/components/NoticeBox'
import simulationApi from '@/api/simulation'
import Modal from '@/components/Modal'
import SimpleModal from '@/components/SimpleModal'
import BridalItemCard from '@/components/BridalItemCard.vue'

const RESPONSE_USAGE_LIMIT_OVER = 490
const RESPONSE_SOMULATION_ADDED = 491

export default {
  metaInfo() {
    return {
      title: this.$store.getters['app/getMenuName']('bridal-item'),
      bodyAttrs: { class: 'bridal-item-favorite' }
    }
  },
  components: {
    NoticeBox,
    Modal,
    SimpleModal,
    BridalItemCard,
  },
  data() {
    return {
      favoriteItemList: [],
      favoriteItemModalTargetId: 0,
    }
  },
  computed: {
    businessId() {
      return this.$store.getters['auth/businessId']
    },
    // お気に入り一覧をグループ・サブグループ・アイテムの3階層に整形して返す
    // （グループ名、サブグループ名の見出しを表示するのに使用）
    favoriteList() {
      let result = []
      for (const row of this.favoriteItemList) {
        const groupId = row.bridal_item_group_id
        const subgroupId = row.bridal_item_subgroup_id
        if (result.find((row) => row.groupId === groupId) === undefined) {
          result.push({
            groupId: groupId,
            groupName: row.bridal_item_group_name,
            subgroupList: [],
          })
        }
        const groupIndex = result.length - 1
        if (
          result[groupIndex].subgroupList.find(
            (row) => row.subgroupId === subgroupId
          ) === undefined
        ) {
          result[groupIndex].subgroupList.push({
            subgroupId: subgroupId,
            subgroupName: row.bridal_item_subgroup_name,
            itemList: [],
          })
        }
        const subgroupIndex = result[groupIndex].subgroupList.length - 1
        result[groupIndex].subgroupList[subgroupIndex].itemList.push(row)
      }
      return result
    },
    // お気に入り一覧から指定したIDのアイテム1件を取り出し、
    // BridalItemCard.vueに渡せる形に整形
    favoriteItem() {
      return function(id) {
        let result = {
          bridal_item_id: 0,
          bridal_item_name: '',
          image_path: '',
          bridal_item_description: '',
          retail_price: 0,
          alert_message: null,
          coordinate_id: null,
          collage_display_target_flag: false,
          simulation_fix_flag: false,
          favorited: false,
          simulation_target: false,
          collage_target: false,
          revision: 0,
          simulation_revision: 0,
          loading: false,
        }
        const selectedItem = this.favoriteItemList.find(
          row => row.bridal_item_id === id
        )
        if (!selectedItem) {
          return result
        }
        result.bridal_item_id = selectedItem.bridal_item_id
        result.bridal_item_name = selectedItem.bridal_item_name
        result.image_path = selectedItem.image_path
        result.bridal_item_description = selectedItem.bridal_item_description
        result.retail_price = selectedItem.retail_price
        result.alert_message = selectedItem.alert_message
        result.coordinate_id = selectedItem.coordinate_id
        result.collage_display_target_flag
          = selectedItem.collage_display_target_flag
        result.simulation_fix_flag = selectedItem.simulation_fix_flag
        result.favorited = selectedItem.favorited
        result.simulation_target = selectedItem.simulation_target
        result.collage_target = selectedItem.simulation_collage_target_flag
        result.revision = selectedItem.revision
        result.simulation_revision = selectedItem.simulation_revision
        result.loading = selectedItem.loading

        return result
      }
    },
    isUsageLimitOver() {
      return this.$store.getters['auth/isCustomer']
        && this.$store.getters['app/getUsageLimitFlag']
        && this.$store.getters['app/getUsageLimitDays'] === 0
    },
  },
  methods: {
    async fetchFavorite() {
      try {
        const response = await bridalItemApi.getFavorite(this.businessId)
        this.favoriteItemList = response.map(row => {
          // BridalItemCard,vueに必要な項目を追加
          row.favorited = true
          row.loading = false
          return row
        })
      } catch (err) {
        renderError(err)
        return
      }
    },
    showFavoriteModal(id) {
      this.favoriteItemModalTargetId = id
      this.$refs.favoriteItemModal.show()
    },
    async onToggleFavorite(item) {
      const favorited = !item.favorited
      // ON/OFF状態を変更し、API更新が終わるまでアイコン押下不可にする
      this.favoriteItemList = this.favoriteItemList.map(row => {
        if (row.bridal_item_id === item.bridal_item_id) {
          row.favorited = favorited
          row.loading = true
        }
        return row
      })
      try {
        const response = await bridalItemApi.updateFavorited(
          this.businessId,
          {
            bridal_item_id: item.bridal_item_id,
            coordinate_id: item.coordinate_id || 0,
            favorited: favorited,
            revision: item.revision,
          }
        )
        // リビジョン更新し、アイコン押下可能に戻す
        this.favoriteItemList = this.favoriteItemList.map(row => {
          if (row.bridal_item_id === item.bridal_item_id) {
            row.revision = response.revision
            row.loading = false
          }
          return row
        })
      } catch (err) {
        renderError(err, {onErrorPopupClose: () => {
          // 利用制限エラーor見積追加済エラーの場合、画面リロード
          if (
            [
              RESPONSE_USAGE_LIMIT_OVER,
              RESPONSE_SOMULATION_ADDED,
            ].includes(err.response.status)
          ) {
            this.$router.go({
              path: this.$router.currentRoute.path,
              force: true,
            })
          }
        }})
        // ON/OFF状態を元に戻し、アイコン押下可能に戻す
        this.favoriteItemList = this.favoriteItemList.map(row => {
          if (row.bridal_item_id === item.bridal_item_id) {
            row.favorited = !favorited
            row.loading = false
          }
          return row
        })
      }
    },
    async onToggleSimulation(item) {
      if (item.simulation_fix_flag) {
        this.$modal.show('simulation-fixed')
        return
      }
      const target = !item.simulation_target
      // ON/OFF状態を変更し、API更新が終わるまでアイコン押下不可にする
      this.favoriteItemList = this.favoriteItemList.map(row => {
        if (row.bridal_item_id === item.bridal_item_id) {
          row.simulation_target = target
          row.loading = true
        }
        return row
      })
      try {
        const response = await simulationApi.updateSimulationTarget(
          this.businessId,
          {
            bridal_item_id: item.bridal_item_id,
            target: target,
            revision: item.simulation_revision || 0,
          }
        )
        // リビジョン更新し、アイコン押下可能に戻す
        // 自身および他アイテムのコラージュアイコン状態も変わるケースあり
        for (const responseRow of response.simulation_bridal_item_list) {
          this.favoriteItemList = this.favoriteItemList.map(row => {
            if (row.bridal_item_id === responseRow.bridal_item_id) {
              row.simulation_collage_target_flag
                = responseRow.collage_target_flag
              row.simulation_revision = responseRow.revision
              row.loading = false
            }
            return row
          })
        }
      } catch (err) {
        renderError(err, {onErrorPopupClose: () => {
          // 利用制限エラーの場合、画面リロード
          if (err.response.status === RESPONSE_USAGE_LIMIT_OVER) {
            this.$router.go({
              path: this.$router.currentRoute.path,
              force: true,
            })
          }
        }})
        // ON/OFF状態を元に戻し、アイコン押下可能に戻す
        this.favoriteItemList = this.favoriteItemList.map(row => {
          if (row.bridal_item_id === item.bridal_item_id) {
            row.simulation_target = !target
            row.loading = false
          }
          return row
        })
      }
    },
    async onToggleCollage(item) {
      const target = !item.simulation_collage_target_flag
      // ON/OFF状態を変更し、API更新が終わるまでアイコン押下不可にする
      this.favoriteItemList = this.favoriteItemList.map(row => {
        if (row.bridal_item_id === item.bridal_item_id) {
          row.simulation_collage_target_flag = target
          row.loading = true
        }
        return row
      })
      try {
        const response = await simulationApi.updateCollageTarget(
          this.businessId,
          {
            bridal_item_id: item.bridal_item_id,
            revision: item.simulation_revision,
          }
        )
        // リビジョン更新し、アイコン押下可能に戻す
        // 自身および他アイテムのコラージュアイコン状態も変わるケースあり
        for (const responseRow of response.simulation_bridal_item_list) {
          this.favoriteItemList = this.favoriteItemList.map(row => {
            if (row.bridal_item_id === responseRow.bridal_item_id) {
              row.simulation_collage_target_flag
                = responseRow.collage_target_flag
              row.simulation_revision = responseRow.revision
              row.loading = false
            }
            return row
          })
        }
        // 同時操作によりコラージュアイコンONのアイテムが複数に
        // なってしまった場合、排他制御エラーにして画面リロードさせる
        const currentItem = this.favoriteItemList.find(row =>
          row.bridal_item_id === item.bridal_item_id
        )
        if (this.favoriteItemList.filter(row =>
          row.bridal_item_subgroup_id === currentItem.bridal_item_subgroup_id
          && row.simulation_target
          && row.simulation_collage_target_flag
        ).length > 1) {
          renderExeclusiveControlError()
        }
      } catch (err) {
        renderError(err, {onErrorPopupClose: () => {
          // 利用制限エラーの場合、画面リロード
          if (err.response.status === RESPONSE_USAGE_LIMIT_OVER) {
            this.$router.go({
              path: this.$router.currentRoute.path,
              force: true,
            })
          }
        }})
        // ON/OFF状態を元に戻し、アイコン押下可能に戻す
        this.favoriteItemList = this.favoriteItemList.map(row => {
          if (row.bridal_item_id === item.bridal_item_id) {
            row.simulation_collage_target_flag = !target
            row.loading = false
          }
          return row
        })
      }
    },
  },
  created() {
    this.$withLoading(this.fetchFavorite)
  }
}
</script>
